/* eslint-disable */
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import i18n from './i18n';

import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { buildPrimeVueLocaleEsCL } from '@/utils/primevueLocaleEsCL';

import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import store from './store/index';

import { createPinia } from 'pinia';

import axios from 'axios';
import { installFrontUsageTelemetry } from '@/plugins/frontUsageTelemetry';
import { installErrorNotebook } from '@/plugins/errorNotebook';
import { sessionStore } from '@/store/session.js';
import { useAppearanceStore } from '@/store/appearance.js';
import AuthService from '@/services/AuthService.js';

function shopTokenFromEnv() {
  const raw = process.env.VUE_APP_SHOP_TOKEN;
  if (raw == null) {
    return '';
  }
  return String(raw)
    .trim()
    .replace(/^['"]/, '')
    .replace(/['"]$/, '');
}

const app = createApp(App).use(i18n).use(router);
installFrontUsageTelemetry(router);
const pinia = createPinia();

app.use(PrimeVue, { locale: buildPrimeVueLocaleEsCL() });
app.use(store);
app.use(ToastService);
app.use(pinia);

installErrorNotebook(app, store, router);

sessionStore().hydrateFromLocalStorage();
useAppearanceStore().hydrateFromStorage();

import '@/assets/css/primevue-custom-theme.css';

router.isReady().then(() => {
  app.mount('#app');
});

const shopToken = shopTokenFromEnv();
axios.defaults.headers.common['shp'] = shopToken;

let oauthRefreshPromise = null;

axios.interceptors.request.use(
  function (config) {
    const method = (config.method || 'get').toUpperCase();
    if (method === 'OPTIONS') {
      return config;
    }
    const token = shopTokenFromEnv();
    if (!config.headers) {
      config.headers = {};
    }
    if (typeof config.headers.set === 'function') {
      config.headers.set('shp', token);
    } else {
      config.headers.shp = token;
    }
    const url = String(config.url || '');
    if (!url.includes('/oauth2/token')) {
      const access = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : '';
      if (access) {
        if (typeof config.headers.set === 'function') {
          config.headers.set('Authorization', `Bearer ${access}`);
        } else {
          config.headers.Authorization = `Bearer ${access}`;
        }
      }
    } else {
      if (typeof config.headers.set === 'function') {
        config.headers.delete('Authorization');
      } else {
        delete config.headers.Authorization;
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const res = error.response;
    const cfg = error.config;
    if (!res || res.status !== 401 || !cfg || cfg.__oauthRetry) {
      return Promise.reject(error);
    }
    const url = String(cfg.url || '');
    if (url.includes('/oauth2/token')) {
      return Promise.reject(error);
    }
    const rt = typeof localStorage !== 'undefined' ? localStorage.getItem('refresh_token') : '';
    if (!rt) {
      sessionStore().clearSession();
      return Promise.reject(error);
    }
    if (!oauthRefreshPromise) {
      oauthRefreshPromise = AuthService.refresh_access(rt)
        .then((r) => {
          sessionStore().sync(r.data);
        })
        .catch((e) => {
          sessionStore().clearSession();
          throw e;
        })
        .finally(() => {
          oauthRefreshPromise = null;
        });
    }
    try {
      await oauthRefreshPromise;
    } catch (e) {
      return Promise.reject(e);
    }
    cfg.__oauthRetry = true;
    return axios(cfg);
  }
);
